/**
 * @file   src/utils/countryHelper.ts
 * @brief  Country registry and locale helpers for multi-country PWA support.
 *         Keep in sync with CarWash_Api/src/helpers/countryHelper.ts registry values.
 */

export type PlatformFeeCollection = 'ach' | 'offline_invoice';

export interface CountryConfig {
  countryCode: string;
  currency: string;
  dialCode: string;
  phoneRegex: RegExp;
  phoneMaxLength: number;
  stateLabel: string;
  postalLabel: string;
  dateFormat: string;
  defaultTimezone: string;
  stripeCountry: string;
  platformFeeCollection: PlatformFeeCollection;
  locale: string;
}

export interface PublicLocaleMeta {
  countryCode: string;
  currency: string;
  locale: string;
  timezone: string;
  dialCode: string;
  dateFormat: string;
  stateLabel: string;
  postalLabel: string;
  stripeCountry: string;
  platformFeeCollection: PlatformFeeCollection;
}

const COUNTRY_REGISTRY: Record<string, CountryConfig> = {
  US: {
    countryCode: 'US',
    currency: 'usd',
    dialCode: '+1',
    phoneRegex: /^\(\d{3}\) \d{3}-\d{4}$/,
    phoneMaxLength: 14,
    stateLabel: 'State',
    postalLabel: 'ZIP Code',
    dateFormat: 'MM/DD/YYYY',
    defaultTimezone: 'America/New_York',
    stripeCountry: 'US',
    platformFeeCollection: 'ach',
    locale: 'en',
  },
  CA: {
    countryCode: 'CA',
    currency: 'cad',
    dialCode: '+1',
    phoneRegex: /^\(\d{3}\) \d{3}-\d{4}$/,
    phoneMaxLength: 14,
    stateLabel: 'Province',
    postalLabel: 'Postal Code',
    dateFormat: 'DD/MM/YYYY',
    defaultTimezone: 'America/Toronto',
    stripeCountry: 'CA',
    platformFeeCollection: 'offline_invoice',
    locale: 'en',
  },
  AU: {
    countryCode: 'AU',
    currency: 'aud',
    dialCode: '+61',
    phoneRegex: /^\d{4} \d{3} \d{3}$/,
    phoneMaxLength: 12,
    stateLabel: 'State/Territory',
    postalLabel: 'Postcode',
    dateFormat: 'DD/MM/YYYY',
    defaultTimezone: 'Australia/Sydney',
    stripeCountry: 'AU',
    platformFeeCollection: 'offline_invoice',
    locale: 'en',
  },
};

export const DEFAULT_COUNTRY_CODE = 'US';
export const LOCALE_STORAGE_KEY = 'CARWASH_ACTIVE_LOCALE';

export function normalizeCountryCode(countryCode?: string | null): string {
  return (countryCode || DEFAULT_COUNTRY_CODE).trim().toUpperCase();
}

export function getCountryConfig(countryCode?: string | null): CountryConfig {
  return COUNTRY_REGISTRY[normalizeCountryCode(countryCode)] || COUNTRY_REGISTRY.US;
}

export function getPhoneRegex(countryCode?: string | null): RegExp {
  return getCountryConfig(countryCode).phoneRegex;
}

export function getPhoneMaxLength(countryCode?: string | null): number {
  return getCountryConfig(countryCode).phoneMaxLength;
}

export function buildPublicLocaleMeta(
  countryCode?: string | null,
  currency?: string | null,
  locale?: string | null,
  timezone?: string | null,
): PublicLocaleMeta {
  const config = getCountryConfig(countryCode);
  return {
    countryCode: normalizeCountryCode(countryCode),
    currency: (currency || config.currency).trim().toLowerCase() || config.currency,
    locale: (locale || config.locale).trim() || config.locale,
    timezone: (timezone || config.defaultTimezone).trim() || config.defaultTimezone,
    dialCode: config.dialCode,
    dateFormat: config.dateFormat,
    stateLabel: config.stateLabel,
    postalLabel: config.postalLabel,
    stripeCountry: config.stripeCountry,
    platformFeeCollection: config.platformFeeCollection,
  };
}

export function setActiveLocale(locale: Partial<PublicLocaleMeta>): PublicLocaleMeta {
  const merged = buildPublicLocaleMeta(
    locale.countryCode,
    locale.currency,
    locale.locale,
    locale.timezone,
  );
  localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

export function getActiveLocale(): PublicLocaleMeta {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return buildPublicLocaleMeta(
        parsed.countryCode,
        parsed.currency,
        parsed.locale,
        parsed.timezone,
      );
    }
  } catch {
    // fall through to company settings
  }

  try {
    const settingsRaw = localStorage.getItem('CARWASH_COMPANY_SETTINGS_DATA');
    if (settingsRaw) {
      const settings = JSON.parse(settingsRaw);
      return buildPublicLocaleMeta(
        settings.countryCode,
        settings.currency,
        settings.locale,
        settings.timezone,
      );
    }
  } catch {
    // fall through
  }

  return buildPublicLocaleMeta();
}

export function getActiveCountryCode(): string {
  return getActiveLocale().countryCode;
}

export function getActiveCurrency(): string {
  return getActiveLocale().currency;
}

export function getActiveStripeCountry(): string {
  return getActiveLocale().stripeCountry;
}

/** Format phone for display based on tenant country. */
export function getCurrencySymbol(currency?: string | null): string {
  const code = (currency || getActiveCurrency()).toLowerCase();
  return CURRENCY_SYMBOLS[code] || '$';
}

export function formatPhoneNumber(phone: string, countryCode?: string | null): string {
  const code = normalizeCountryCode(countryCode || getActiveCountryCode());
  const digits = (phone || '').replace(/\D/g, '');

  if (!digits) {
    return phone || '';
  }

  if (code === 'AU') {
    let national = digits.startsWith('61') && digits.length > 10 ? digits.slice(2) : digits;

    // API stores mobiles/landlines without the trunk 0 (9 digits). Restore it for
    // display only when the user did not already type/keep a leading 0 — otherwise
    // backspace from a full number keeps injecting zeros and blocks new input.
    if (national.length === 9 && !national.startsWith('0') && !national.startsWith('1')) {
      national = `0${national}`;
    }

    if (national.length > 10) {
      national = national.slice(0, 10);
    }

    if (national.length > 7) {
      return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`.trim();
    }
    if (national.length > 4) {
      return `${national.slice(0, 4)} ${national.slice(4)}`.trim();
    }
    return national;
  }

  // US and CA share NANP display: (XXX) XXX-XXXX
  if (code === 'US' || code === 'CA') {
    let national = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
    if (national.length > 10) {
      national = national.slice(-10);
    }

    if (national.length >= 7) {
      return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
    }
    if (national.length >= 4) {
      return `(${national.slice(0, 3)}) ${national.slice(3)}`;
    }
    return national;
  }

  return phone;
}

/** Strip phone to national digits for API (AU drops +61, keeps leading 0; US/CA drop +1). */
export function formatPhoneNumberforapipass(phoneNumber: string, countryCode?: string | null): string {
  let digits = (phoneNumber || '').replace(/\D/g, '');
  const code = normalizeCountryCode(countryCode || getActiveCountryCode());

  if (code === 'AU') {
    if (digits.startsWith('61') && digits.length > 9) {
      digits = digits.slice(2);
    }
  } else if (code === 'US' || code === 'CA') {
    if (digits.startsWith('1') && digits.length > 10) {
      digits = digits.slice(1);
    }
    if (digits.length > 10) {
      digits = digits.slice(-10);
    }
  } else if (digits.length > 10) {
    digits = digits.slice(-10);
  }

  return digits;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  cad: '$',
  aud: '$',
};

export function formatMoney(amount: number | string | null | undefined, currency?: string | null): string {
  const value = Number(amount);
  const safeAmount = Number.isFinite(value) ? value : 0;
  const code = (currency || getActiveCurrency()).toLowerCase();

  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code.toUpperCase(),
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeAmount);
  } catch {
    const symbol = CURRENCY_SYMBOLS[code] || '$';
    return `${symbol}${safeAmount.toFixed(2)}`;
  }
}

export function formatMoneyParts(amount: number | string | null | undefined, currency?: string | null): { dollars: string; cents: string } {
  const formatted = formatMoney(amount, currency);
  const numeric = formatted.replace(/[^\d.,-]/g, '');
  const parts = numeric.split(/[.,]/);
  return {
    dollars: parts[0] || '0',
    cents: (parts[1] || '00').slice(0, 2).padEnd(2, '0'),
  };
}

export default COUNTRY_REGISTRY;
